import { Dtos, type Rate, SuretyQuoteStatus } from "@inrev/common";
import { useAdminGetCarrierPreviews } from "@portal/api/account/admin";
import { useAdminFetchCarrierAgencyRelationships } from "@portal/api/bond-request/admin";
import {
	AdminBondRequest,
	AdminUpdateBondRequestFormData,
} from "@portal/api/bond-request/admin/types";
import { FormField, FormFieldGroup, FormItemLabel, FormRow } from "@portal/components/form";
import { FormDropdown, FormPercent } from "@portal/components/form/fields";
import { Modal } from "@portal/components/layout/Modal";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { cn } from "@portal/components/lib/utils";
import { Button } from "@portal/components/ui/Button";
import { useEffect } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";
import { stripEmptyResolver, typedZodResolver } from "../../../utils/form";
import {
	AdminApproveBondFormFormFields,
	getDefaultFormData as getApproveBondFormDefaultFormData,
} from "./AdminApproveBondFormForAgentIssueForm";
import { QuoteStatusUpdate } from "./AdminBondRequestStatusTransitionDropdown";

type AdminChangeBondStatusModalProps = {
	request: AdminBondRequest;
	/** Required when approving; unused for review/declined confirmations. */
	bondForm: Dtos.Admin.SuretyBondForm.Get.Response | undefined;
	accountRate: Rate | undefined;
	onUpdate: ({ data }: { data: Dtos.Admin.SuretyQuote.UpdateStatus.Request }) => void;
	onClose: (status?: SuretyQuoteStatus) => void;
	status: QuoteStatusUpdate;
};

const getDefaultFormData = (
	request: AdminBondRequest,
	bondForm: Dtos.Admin.SuretyBondForm.Get.Response | undefined,
	status: QuoteStatusUpdate,
	shouldApproveBondFormForAgentIssue: boolean,
	accountRate: Rate | undefined,
): AdminUpdateBondRequestFormData => ({
	suretyType: request.suretyType,
	contractSuretyType: request.contractSuretyType,
	status,
	carrierName: request.carrier?.name ?? "",
	rate: request.rate ?? accountRate ?? undefined,
	finalBondRate:
		request.contractSuretyType === "bid"
			? (request.finalBondContingentRate ?? accountRate ?? undefined)
			: (accountRate ?? undefined),
	agencyCommissionPercentage: request.agencyCommissionPercentage ?? undefined,
	reviewNote: request.reviewNote ?? "",
	...(() => {
		if (shouldApproveBondFormForAgentIssue && bondForm) {
			return {
				bondForm: getApproveBondFormDefaultFormData(request, bondForm),
			};
		}
		return {};
	})(),
});
export const AdminChangeBondStatusModal = ({
	request,
	bondForm,
	accountRate,
	onUpdate,
	onClose,
	status,
}: AdminChangeBondStatusModalProps) => {
	const { allCarriers } = useAdminGetCarrierPreviews();
	const { carrierAgencyRelationships } = useAdminFetchCarrierAgencyRelationships(request.agencyId);

	const shouldApproveBondFormForAgentIssue =
		bondForm !== undefined &&
		bondForm.source === "upload" &&
		(bondForm.attributes.cancellable == null ||
			bondForm.attributes.continuous == null ||
			bondForm.attributes.renewable == null);

	const formMethods = useForm<
		AdminUpdateBondRequestFormData,
		any,
		Dtos.Admin.SuretyQuote.UpdateStatus.Request
	>({
		defaultValues: getDefaultFormData(
			request,
			bondForm,
			status,
			shouldApproveBondFormForAgentIssue,
			accountRate,
		),
		reValidateMode: "onBlur",
		resolver: stripEmptyResolver(
			typedZodResolver(Dtos.Admin.SuretyQuote.UpdateStatus.Request.schema),
		),
	});
	const widenedFormMethods: UseFormReturn<AdminUpdateBondRequestFormData, any, any> = formMethods;

	const handleSubmit = (data: Dtos.Admin.SuretyQuote.UpdateStatus.Request) => {
		onUpdate({ data });
		onClose(status);
	};

	const carrier = formMethods.watch("carrierName");
	useEffect(() => {
		if (carrier && !request.agencyCommissionPercentage) {
			const carrierRelationship = carrierAgencyRelationships?.find(
				(cr) => cr.carrierName === carrier,
			);
			formMethods.setValue(
				"agencyCommissionPercentage",
				carrierRelationship?.agencyCommissionPercentage ?? undefined,
			);
		}
	}, [carrier, formMethods.setValue, carrierAgencyRelationships]);
	return (
		<Modal onClickOutside={onClose}>
			<ModalItemWithHeader
				header="Change Bond Request Status"
				onClose={onClose}
				bodyClassName="pt-[20px]"
			>
				<div className={cn("px-[40px] pb-[30px]")}>
					<form>
						<FormFieldGroup>
							{status === "approved" && allCarriers && (
								<>
									<FormField>
										<FormItemLabel required>Carrier</FormItemLabel>
										<FormDropdown
											name="carrierName"
											control={widenedFormMethods.control}
											options={allCarriers.map((carrier) => ({
												value: carrier.name,
												label: carrier.legalName,
											}))}
											placeholder="Select one"
											compact
										/>
									</FormField>
									<FormRow>
										<FormField>
											<FormItemLabel required>
												{request.contractSuretyType === "bid" ? "Final Bond Rate" : "Rate"}
											</FormItemLabel>
											<FormPercent
												name={request.contractSuretyType === "bid" ? "finalBondRate" : "rate"}
												control={widenedFormMethods.control}
												compact
												decimalPlaces={5}
											/>
										</FormField>
										<FormField>
											<FormItemLabel required>Agency Commission</FormItemLabel>
											<FormPercent
												name="agencyCommissionPercentage"
												control={widenedFormMethods.control}
												compact
											/>
										</FormField>
									</FormRow>
									{shouldApproveBondFormForAgentIssue && (
										<AdminApproveBondFormFormFields
											formMethods={formMethods}
											fieldPaths={{
												bondFormType: "bondForm.bondFormType",
												cancellable: "bondForm.cancellable",
												continuous: "bondForm.continuous",
											}}
											request={request}
										/>
									)}
								</>
							)}
							{status !== "approved" && (
								<div className="text-[16px] text-tertiary mt-[5px] italic">
									Are you sure you want to change the status to{" "}
									<span
										className={cn(
											"rounded-[4px] px-[7px] py-[3px]",
											status === "review"
												? "bg-intent-warning-bg text-intent-warning-text"
												: status === "declined"
													? "bg-danger bg-opacity-[0.08] text-danger"
													: "text-tertiary",
										)}
									>
										{status}
									</span>
									?
								</div>
							)}
							<div className="flex w-full justify-center pt-[10px] space-x-[10px] gap-2">
								<Button
									color={
										status === "approved" ? "confirm" : status === "declined" ? "danger" : "primary"
									}
									className="px-[25px] py-[12px] order-2"
									filled
									onClick={formMethods.handleSubmit(handleSubmit)}
								>
									{status === "approved" ? "Approve & " : status === "declined" ? "Decline & " : ""}
									Save
								</Button>
								<Button
									onClick={() => onClose(request.status)}
									color="secondary"
									className="w-[80px] order-1"
									thinFont
								>
									Cancel
								</Button>
							</div>
						</FormFieldGroup>
					</form>
				</div>
			</ModalItemWithHeader>
		</Modal>
	);
};
